import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import ChartContainer from '../ChartContainer';

// Deterministic soft color per label (HSL) for a clean, professional palette
function stringToColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const sat = 55; // softer saturation
  const light = 62; // mid-lightness for good contrast
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

// Pack intervals into the minimum number of non-overlapping rows (greedy)
function packIntervals(segments) {
  // segments: [{ start: DateTime, end: DateTime, ... }]
  const sorted = [...segments].sort((a, b) => a.start.toMillis() - b.start.toMillis());
  const rowEnds = []; // row index -> last end millis
  const placed = [];
  for (const seg of sorted) {
    const s = seg.start.toMillis();
    const e = seg.end.toMillis();
    let row = -1;
    for (let i = 0; i < rowEnds.length; i++) {
      if (s >= rowEnds[i]) { row = i; break; }
    }
    if (row === -1) { row = rowEnds.length; rowEnds.push(0); }
    rowEnds[row] = Math.max(rowEnds[row], e);
    placed.push({ ...seg, _row: row });
  }
  return { placed, rows: rowEnds.length };
}

// Show every activity change as an exact segment; no masking or aggregation
export default function WorkerActivitiesTimeline({ events, height = 'auto', bucketMinutes = 5, onTaskClick, allowedQueues, teamFilter, singleWorker, selectedWorkers = [], stackCalls = false, singleClient = '', selectedClients = [] }) {
  const [hoverLabel, setHoverLabel] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, lines: [] });
  // Legend hover state with category awareness; e.g., { category: 'call'|'mecca', status: 'handled'|'missed'|... }
  const [hoverLegend, setHoverLegend] = useState(null);
  const [domain, setDomain] = useState({ startMs: 0, endMs: 1 });
  const [view, setView] = useState({ startMs: 0, endMs: 1 });
  const [drag, setDrag] = useState({ active: false, startX: 0, currentX: 0 });
  const [hoverTaskWorkers, setHoverTaskWorkers] = useState(null); // Set of worker names
  const [hoverTaskWindow, setHoverTaskWindow] = useState(null);   // { startMs, endMs }
  const [hoverTaskWorkerRoles, setHoverTaskWorkerRoles] = useState(null); // Map(worker -> { role, color })
  const [hoverWorkerTarget, setHoverWorkerTarget] = useState(null); // { worker, startMs, endMs }
  const [dragTasks, setDragTasks] = useState({ active: false, startX: 0, currentX: 0 });
  // Grid-level focus: when hovering a single segment, dim all others in that table
  const [hoverTaskSegKey, setHoverTaskSegKey] = useState(null);
  const [hoverAgentSegKey, setHoverAgentSegKey] = useState(null);
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const tasksContainerRef = useRef(null);
  const tasksGridRef = useRef(null);
  const tooltipElRef = useRef(null);
  const tooltipRafRef = useRef(null);
  const LEFT_COL_PX = 180;
  const HEADER_PX = 34; // match existing header height used elsewhere

  // Keep tooltip within viewport
  const placeTooltip = (clientX, clientY) => {
    const margin = 6; // keep it close under the cursor
    const pad = 6; // container padding
    const maxW = 360; // must match container style below
    const maxH = 220; // typical content height
    const rect = rootRef.current?.getBoundingClientRect();
    const originX = rect ? clientX - rect.left : clientX;
    const originY = rect ? clientY - rect.top : clientY;
    const vw = rect?.width ?? (typeof window !== 'undefined' ? window.innerWidth : 1200);
    const vh = rect?.height ?? (typeof window !== 'undefined' ? window.innerHeight : 800);
    // Horizontal placement relative to wrapper
    let x = originX + margin;
    if (x + maxW > vw - pad) x = Math.max(pad, originX - margin - maxW);
    // Vertical placement relative to wrapper
    let y = originY + margin;
    if (y + maxH > vh - pad) y = Math.max(pad, originY - margin - maxH);
    return { x, y };
  };

  // Smoothly move tooltip without causing full re-render on mouse move
  const moveTooltip = (clientX, clientY) => {
    if (!tooltip.visible || !tooltipElRef.current) return;
    const { x, y } = placeTooltip(clientX, clientY);
    if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current);
    tooltipRafRef.current = requestAnimationFrame(() => {
      if (!tooltipElRef.current) return;
      tooltipElRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    });
  };

  useEffect(() => () => { if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current); }, [tooltip.visible]);

  // Global safety net: if the window loses focus or pointer leaves the document, clear legend hover
  useEffect(() => {
    const clearLegend = () => { setHoverLegend(null); setHoverLabel(null); };
    const onVis = () => { if (document.hidden) clearLegend(); };
    window.addEventListener('blur', clearLegend);
    document.addEventListener('visibilitychange', onVis);
    document.addEventListener('pointerleave', clearLegend);
    document.addEventListener('mouseleave', clearLegend);
    return () => {
      window.removeEventListener('blur', clearLegend);
      document.removeEventListener('visibilitychange', onVis);
      document.removeEventListener('pointerleave', clearLegend);
      document.removeEventListener('mouseleave', clearLegend);
    };
  }, []);

  const { workers, segmentsByWorker, labels, ticks, tStartMs, tSpanMs, workerTeams, taskSegments } = useMemo(() => {
    if (!events || events.length === 0) {
      return { workers: [], segmentsByWorker: {}, labels: new Set(), ticks: [], tStartMs: 0, tSpanMs: 1, workerTeams: {}, taskSegments: [] };
    }

    const updates = events.filter(e => e.eventType === 'worker.activity.update' && e.worker);
    if (updates.length === 0) {
      return { workers: [], segmentsByWorker: {}, labels: new Set(), ticks: [], tStartMs: 0, tSpanMs: 1, workerTeams: {}, taskSegments: [] };
    }

    const times = updates.map(e => DateTime.fromISO(e.timestamp)).filter(t => t.isValid);
    const minTime = DateTime.min(...times);
    const maxTime = DateTime.max(...times);
    const start = minTime;
    const end = maxTime.plus({ minutes: 1 });
    const domainStartMs = start.toMillis();
    const domainEndMs = end.toMillis();
    const tStartMs = view.startMs || domainStartMs;
    const tSpanMs = Math.max(1, (view.endMs || domainEndMs) - (view.startMs || domainStartMs));

    const workers = Array.from(new Set(updates.map(e => e.worker))).sort();
    const workerTeams = {};
    const byWorker = Object.fromEntries(workers.map(w => [w, []]));
    for (const e of updates) {
      const t = DateTime.fromISO(e.timestamp);
      if (!t.isValid) continue;
      byWorker[e.worker].push({ t, label: e.currentActivity || 'Unknown' });
      if (!workerTeams[e.worker]) {
        const team = e.workerTeamName
          || e.workerAttributes?.team_name
          || e.workerAttributes?.team
          || e.workerAttributes?.teamName
          || '—';
        workerTeams[e.worker] = team;
      }
    }
    for (const w of workers) byWorker[w].sort((a, b) => a.t - b.t);

  const segmentsByWorker = Object.fromEntries(workers.map(w => [w, []]));
  const labels = new Set();
    for (const w of workers) {
      const ups = byWorker[w];
      for (let i = 0; i < ups.length; i++) {
        const cur = ups[i];
        const next = ups[i + 1];
        const segStart = cur.t;
        const segEnd = next ? next.t : end;
        if (segEnd <= segStart) continue;
        const name = cur.label || '';
        const isLogoff = name.trim().toLowerCase() === 'logoff' || name.trim().toLowerCase() === 'log off' || name.trim().toLowerCase() === 'logged off';
        if (isLogoff) {
          // Skip rendering of logoff segments entirely
          continue;
        }
        labels.add(name);
        segmentsByWorker[w].push({ start: segStart, end: segEnd, label: name });
      }
    }

  // Compute "nice" ticks aligned to step boundaries for clear labels (quarters/tens/half-hours)
  const spanMinutes = (tSpanMs / 60000);
  // Prefer quarters for mid/large spans; otherwise smaller sensible steps
  let step = 1; // minutes
  if (spanMinutes > 720) step = 60; // 12h+
  else if (spanMinutes > 360) step = 30; // 6h+
  else if (spanMinutes > 120) step = 15; // 2h+
  else if (spanMinutes > 60) step = 10;  // 1h+
  else if (spanMinutes > 30) step = 5;   // 30m+
  else if (spanMinutes > 10) step = 2;   // 10m+
  // Align start tick to next step boundary to avoid odd numbers like 11:03
  const ticks = [];
  const viewStart = DateTime.fromMillis(tStartMs).startOf('minute');
  const endTickDt = DateTime.fromMillis(tStartMs + tSpanMs);
  // Move to the next minute where minute % step === 0
  let tick = viewStart;
  const remainder = tick.minute % step;
  if (remainder !== 0) tick = tick.plus({ minutes: (step - remainder) });
  // If aligning pushed us before the real domain (rare negative), clamp forward
  if (tick < viewStart) tick = viewStart;
  while (tick <= endTickDt) { ticks.push(tick); tick = tick.plus({ minutes: step }); }

    // Build task/call segments (aggregate by taskSid)
    const byTask = new Map();
    for (const ev of events) {
      const ti = ev.taskInfo || {};
      const sid = ti.taskSid || ti.sid || ev.payload?.task_sid || ev.payload?.taskSid;
      if (!sid) continue;
      const t = DateTime.fromISO(ev.timestamp);
      if (!t.isValid) continue;
      if (!byTask.has(sid)) {
        // attributes can be in taskInfo.attributes or payload.task_attributes
        let attrs = ti.attributes ?? ev.payload?.task_attributes ?? ev.payload?.attributes ?? {};
        if (typeof attrs === 'string') {
          try { attrs = JSON.parse(attrs); } catch { /* ignore parse errors */ }
        }
        byTask.set(sid, {
          sid,
          type: ti.type || ev.payload?.task_channel_unique_name || ev.payload?.task_channel || '',
          queue: ti.taskQueue || ev.payload?.task_queue_name || '—',
          attributes: attrs || {},
          created: null,
          acceptedBy: null,
          acceptedAt: null,
          wrapupAt: null,
          completedAt: null,
          canceledAt: null,
          rejections: [], // { worker, at }
          timeouts: [],   // { worker, at }
          firstTs: t,
          lastTs: t,
        });
      }
      const rec = byTask.get(sid);
      rec.firstTs = t < rec.firstTs ? t : rec.firstTs;
      rec.lastTs = t > rec.lastTs ? t : rec.lastTs;
      const et = ev.eventType || '';
      if (et === 'task.created') {
        rec.created = rec.created ? DateTime.min(rec.created, t) : t;
      } else if (et === 'reservation.accepted') {
        // consider the first acceptance as handler
        if (!rec.acceptedAt || t < rec.acceptedAt) {
          rec.acceptedAt = t;
          rec.acceptedBy = ev.worker || ev.payload?.worker_name || null;
        }
      } else if (et === 'reservation.completed' || et === 'task.completed') {
        rec.completedAt = rec.completedAt ? DateTime.max(rec.completedAt, t) : t;
      } else if (et === 'task.wrapup') {
        rec.wrapupAt = rec.wrapupAt ? DateTime.min(rec.wrapupAt, t) : t;
      } else if (et === 'task.canceled' || et === 'task.canceled') {
        rec.canceledAt = rec.canceledAt ? DateTime.max(rec.canceledAt, t) : t;
      } else if (et === 'reservation.rejected') {
        rec.rejections.push({ worker: ev.worker || ev.payload?.worker_name || '—', at: t });
      } else if (et === 'reservation.timeout') {
        rec.timeouts.push({ worker: ev.worker || ev.payload?.worker_name || '—', at: t });
      }
    }

    const statusOf = (r) => {
      if (r.canceledAt) return 'canceled';
      if (r.completedAt) return 'handled';
      if (r.acceptedAt && !r.completedAt) return 'accepted';
      if (r.rejections.length) return 'rejected';
      if (r.timeouts.length) return 'missed';
      return 'unknown';
    };
    const endTimeOf = (r) => r.completedAt || r.canceledAt || r.lastTs;
    const startTimeOf = (r) => r.created || r.firstTs;

  const taskSegments = [];
    for (const rec of byTask.values()) {
      const st = startTimeOf(rec);
      const en = endTimeOf(rec);
      if (!st || !en || en <= st) continue;
  const status = statusOf(rec);
  const direction = rec.attributes?.direction || '';
      const from = rec.attributes?.from || '';
      const to = rec.attributes?.direction === 'outbound' ? (rec.attributes?.outbound_to || '') : (rec.attributes?.to || '');
      const taskSubject = (rec.attributes?.taskSubject && String(rec.attributes.taskSubject)) || '';
      const customerName = (
        rec.attributes?.conversations?.conversation_attribute_3 ||
        rec.attributes?.conversation_attribute_3 ||
        rec.attributes?.customerName ||
        rec.attributes?.customer_name ||
        rec.attributes?.CustomerName ||
        rec.attributes?.customers?.customer_attribute_2 ||
        rec.attributes?.customer ||
        ''
      );
      // Filter out administrative tasks
      const typeLc = (rec.type || '').toLowerCase();
      const queueLc = (rec.queue || '').toLowerCase();
      const attrTypeLc = String(rec.attributes?.type || rec.attributes?.taskType || rec.attributes?.task_type || '').toLowerCase();
      const maybeAdmin = (s) => s.includes('admin') || s.includes('administration') || s.includes('administratie');
      if (maybeAdmin(typeLc) || maybeAdmin(queueLc) || maybeAdmin(attrTypeLc)) {
        continue;
      }
      // Determine category: calls vs mecca tasks
      const isMecca = (() => {
        const t = (rec.type || '').toLowerCase();
        const q = (rec.queue || '').toLowerCase();
        const aType = String(rec.attributes?.type || rec.attributes?.taskType || rec.attributes?.task_type || '').toLowerCase();
        const wName = String(rec.attributes?.workflow_name || '').toLowerCase();
        const channel = String(rec.attributes?.twilio_channel || '').toLowerCase();
        return t.includes('mecca') || q.includes('mecca') || aType.includes('mecca') || wName.includes('mecca') || channel.includes('mecca');
      })();
      const category = direction ? 'call' : (isMecca ? 'mecca' : 'call');

      taskSegments.push({
        sid: rec.sid,
        start: st,
        end: en,
        status,
        type: rec.type,
        queue: rec.queue,
        direction,
        from,
        to,
        taskSubject,
        customerName,
        acceptedBy: rec.acceptedBy,
        rejectedBy: Array.from(new Set(rec.rejections.map(r => r.worker))).filter(Boolean),
        missedBy: Array.from(new Set(rec.timeouts.map(r => r.worker))).filter(Boolean),
        rejections: rec.rejections.map(r => ({ worker: r.worker, at: r.at })),
        timeouts: rec.timeouts.map(r => ({ worker: r.worker, at: r.at })),
        acceptedAt: rec.acceptedAt,
        wrapupAt: rec.wrapupAt,
        category,
      });
    }

    return { workers, segmentsByWorker, labels, ticks, tStartMs, tSpanMs, workerTeams, taskSegments };
  }, [events, view.startMs, view.endMs]);

  // Apply client/customer filters (single exact match and/or multi-select). Case-insensitive exact match.
  const filteredTaskSegments = useMemo(() => {
    const single = (singleClient || '').trim().toLowerCase();
    const multi = new Set((selectedClients || []).map(c => String(c).trim().toLowerCase()).filter(Boolean));
    const useSingle = !!single;
    const useMulti = multi.size > 0;
    if (!useSingle && !useMulti) return taskSegments;
    return (taskSegments || []).filter(seg => {
      const name = ((seg.customerName && String(seg.customerName)) || '').trim().toLowerCase();
      if (!name) return false;
      if (useSingle && useMulti) {
        return name === single || multi.has(name);
      }
      if (useSingle) return name === single;
      // multi only
      return multi.has(name);
    });
  }, [taskSegments, singleClient, selectedClients]);

  // Level-of-detail: when zoomed out (large time span), skip fine-grained overlays/markers
  const spanMinutesNow = Math.max(1, (view.endMs - view.startMs) / 60000);
  const LOD = useMemo(() => ({
    // Treat >= 8 hours or very dense tick counts as coarse view
    coarse: spanMinutesNow >= 480 || (ticks?.length || 0) > 200,
  }), [spanMinutesNow, ticks?.length]);

  // Shared status color map for Tasks/Calls
  // Separate color schemes for calls vs mecca tasks
  const statusColors = {
    callIn: {
      handled: '#16a34a',
      accepted: '#22c55e',
      rejected: '#ef4444',
      missed: '#3b82f6',
      canceled: '#9ca3af',
      unknown: '#9ca3af',
      queued: '#000000'
    },
    callOut: {
      handled: '#0ea5e9',
      accepted: '#38bdf8',
      rejected: '#ef4444',
      missed: '#3b82f6',
      canceled: '#9ca3af',
      unknown: '#9ca3af',
      queued: '#000000'
    },
    mecca: {
      handled: '#8b5cf6',
      accepted: '#a78bfa',
      rejected: '#ef4444',
      missed: '#3b82f6',
      canceled: '#9ca3af',
      unknown: '#9ca3af',
      queued: '#f59e0b'
    }
  };
  // Resolve the correct palette for a segment: calls split by direction, mecca uses its own
  const getPaletteForSeg = (seg) => {
    if (!seg) return statusColors.callIn;
    if (seg.category === 'mecca') return statusColors.mecca;
    // default calls to inbound palette when direction is missing/unknown
    return seg.direction === 'outbound' ? statusColors.callOut : statusColors.callIn;
  };
  const inQueueOverlayFor = (_category) => '#000';

  // Simple cross icon used for rejected/missed in both bars and legend
  const CrossIcon = ({ color, size = 14, stroke = 3, style }) => (
    <div style={{ position: 'relative', width: size, height: size, ...style }}>
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: size, height: stroke, background: color, transform: 'translate(-50%, -50%) rotate(45deg)', borderRadius: 1 }} />
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: size, height: stroke, background: color, transform: 'translate(-50%, -50%) rotate(-45deg)', borderRadius: 1 }} />
    </div>
  );

  const StatusLegend = ({ style, compact = false }) => (
    <div
      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', ...style }}
      onMouseLeave={() => setHoverLegend(null)}
      onPointerLeave={() => setHoverLegend(null)}
    >
      {/* Grouped legend: Calls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginRight: 32 }}>
        <div style={{ fontWeight: 600, color: '#374151' }}>Calls</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* primary status items */}
          {['handled','canceled','rejected','missed'].map(key => (
            <span
              key={key}
              onMouseEnter={() => { if (key !== 'queued') setHoverLegend({ category: 'call', status: key }); }}
              onMouseLeave={() => { if (key !== 'queued') setHoverLegend(null); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: key === 'queued' ? 'default' : 'pointer', opacity: hoverLegend ? ((hoverLegend.category === 'call' && hoverLegend.status === key) ? 1 : 0.4) : 1, fontSize: compact ? 12 : 13, padding: compact ? '3px 6px' : '4px 8px', margin: compact ? '2px 3px' : '3px 4px', borderRadius: 4 }}
              title={`Highlight ${key === 'canceled' ? 'abandoned' : key}`}
            >
              {key === 'rejected' || key === 'missed' ? (
                <CrossIcon color={statusColors.callIn[key]} size={14} stroke={3} />
              ) : key === 'queued' ? (
                <span style={{ width: 16, height: 10, background: inQueueOverlayFor('callIn'), display: 'inline-block', border: `1px solid ${statusColors.callIn.queued}`, borderRadius: 2 }} />
              ) : (
                <span style={{ width: 12, height: 12, background: statusColors.callIn[key], display: 'inline-block', border: '1px solid #e5e7eb' }} />
              )}
              {key === 'queued' ? 'in Queue' : (key === 'canceled' ? 'abandoned' : key)}
            </span>
          ))}
          {/* insert an informational chip for outbound calls color before 'in Queue' */}
          <span
            key="outbound-chip"
            onMouseEnter={() => setHoverLegend({ category: 'call', status: 'outbound' })}
            onMouseLeave={() => setHoverLegend(null)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: hoverLegend ? ((hoverLegend.category === 'call' && hoverLegend.status === 'outbound') ? 1 : 0.4) : 1, fontSize: compact ? 12 : 13, padding: compact ? '3px 6px' : '4px 8px', margin: compact ? '2px 3px' : '3px 4px', borderRadius: 4 }}
            title="Highlight uitgaande calls"
          >
            <span style={{ width: 12, height: 12, background: statusColors.callOut.handled, display: 'inline-block', border: '1px solid #e5e7eb' }} />
            uitgaande calls
          </span>
          {/* queued item at the end */}
          <span
            key="queued"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'default', opacity: 1, fontSize: compact ? 12 : 13, padding: compact ? '3px 6px' : '4px 8px', margin: compact ? '2px 3px' : '3px 4px', borderRadius: 4 }}
            title="in Queue"
          >
            <span style={{ width: 16, height: 10, background: inQueueOverlayFor('callIn'), display: 'inline-block', border: `1px solid ${statusColors.callIn.queued}`, borderRadius: 2 }} />
            in Queue
          </span>
        </div>
      </div>
      {/* Grouped legend: Mecca Taken */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 'auto' }}>
        <div style={{ fontWeight: 600, color: '#374151' }}>Mecca Taken</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {['handled','rejected'].map(key => (
            <span
              key={`mecca-${key}`}
              onMouseEnter={() => { if (key !== 'queued') setHoverLegend({ category: 'mecca', status: key }); }}
              onMouseLeave={() => { if (key !== 'queued') setHoverLegend(null); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: hoverLegend ? ((hoverLegend.category === 'mecca' && hoverLegend.status === key) ? 1 : 0.4) : 1, fontSize: compact ? 12 : 13, padding: compact ? '3px 6px' : '4px 8px', margin: compact ? '2px 3px' : '3px 4px', borderRadius: 4 }}
              title={`Highlight ${key} (Mecca)`}
            >
              {key === 'rejected' || key === 'missed' ? (
                <CrossIcon color={statusColors.mecca[key]} size={14} stroke={3} />
              ) : (
                <span style={{ width: 12, height: 12, background: statusColors.mecca[key], display: 'inline-block', border: '1px solid #e5e7eb' }} />
              )}
              {key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  // Occupancy chart temporarily disabled

  // Helper: duration formatter
  const fmt = (secs) => {
    if (secs == null || !isFinite(secs)) return '—';
    secs = Math.max(0, Math.round(secs));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    return `${m}:${String(s).padStart(2,'0')}`;
  };

  // Categorization helpers
  const lowerIncludes = (name, chunk) => name.toLowerCase().includes(chunk);
  const isCall = (name) => {
    const n = name.trim().toLowerCase();
    return n === 'on a call' || n === 'handling call session' || lowerIncludes(n,'on call');
  };
  const isShortBreak = (name) => {
    const n = name.toLowerCase();
    return lowerIncludes(n,'not ready') && (lowerIncludes(n,'short break') || lowerIncludes(n,'bio') || lowerIncludes(n,'toilet'));
  };
  const isAdminCF = (name) => {
    const n = name.toLowerCase();
    return lowerIncludes(n,'admin internal') && lowerIncludes(n,'customer feedback');
  };
  const isAdminOther = (name) => {
    const n = name.toLowerCase();
    return lowerIncludes(n,'admin internal') && !lowerIncludes(n,'customer feedback');
  };
  // const isNotReady = (name) => name.toLowerCase().includes('not ready'); // unused
  const isReady = (name) => {
    const n = name.trim().toLowerCase();
    if (n.includes('not ready')) return false; // guard
    return n === 'ready' || n === 'available' || n === 'idle';
  };

  // Compute per-worker metrics for the current view window and team averages
  const metrics = useMemo(() => {
    const viewStart = view.startMs, viewEnd = view.endMs;
    const perWorker = {};
    const teamAgg = {}; // team -> cat -> {count,totalSec}
    const cats = ['call','shortBreak','adminCF','adminOther','ready'];
    const catMatch = {
      call: isCall,
      shortBreak: isShortBreak,
      adminCF: isAdminCF,
      adminOther: isAdminOther,
      ready: isReady,
    };
    for (const w of workers) {
      const team = workerTeams[w] || '—';
      if (!perWorker[w]) perWorker[w] = { team, call:{count:0,totalSec:0}, shortBreak:{count:0,totalSec:0}, adminCF:{count:0,totalSec:0}, adminOther:{count:0,totalSec:0}, ready:{count:0,totalSec:0} };
      if (!teamAgg[team]) teamAgg[team] = { call:{count:0,totalSec:0}, shortBreak:{count:0,totalSec:0}, adminCF:{count:0,totalSec:0}, adminOther:{count:0,totalSec:0}, ready:{count:0,totalSec:0} };
      const segs = segmentsByWorker[w] || [];
      for (const seg of segs) {
        const s = Math.max(viewStart, seg.start.toMillis());
        const e = Math.min(viewEnd, seg.end.toMillis());
        if (e <= s) continue;
        const durSec = (e - s) / 1000;
        const name = seg.label || '';
        for (const c of cats) {
          if (catMatch[c](name)) {
            perWorker[w][c].count += 1;
            perWorker[w][c].totalSec += durSec;
            teamAgg[team][c].count += 1;
            teamAgg[team][c].totalSec += durSec;
          }
        }
      }
    }
    // compute averages and team averages
    const toAvg = (obj) => ({
      callAvg: obj.call.count ? obj.call.totalSec / obj.call.count : null,
      shortBreakAvg: obj.shortBreak.count ? obj.shortBreak.totalSec / obj.shortBreak.count : null,
      adminCFAvg: obj.adminCF.count ? obj.adminCF.totalSec / obj.adminCF.count : null,
      adminOtherAvg: obj.adminOther.count ? obj.adminOther.totalSec / obj.adminOther.count : null,
      readyAvg: obj.ready.count ? obj.ready.totalSec / obj.ready.count : null,
    });
    const teamAvg = {};
    for (const [team, agg] of Object.entries(teamAgg)) teamAvg[team] = toAvg(agg);
    // enrich perWorker with avgs and deltas
    for (const [w, rec] of Object.entries(perWorker)) {
      const av = toAvg(rec);
      const t = teamAvg[rec.team] || {};
      rec.avgs = av;
      rec.deltas = {
        call: av.callAvg == null || t.callAvg == null ? null : av.callAvg - t.callAvg,
        shortBreak: av.shortBreakAvg == null || t.shortBreakAvg == null ? null : av.shortBreakAvg - t.shortBreakAvg,
        adminCF: av.adminCFAvg == null || t.adminCFAvg == null ? null : av.adminCFAvg - t.adminCFAvg,
        adminOther: av.adminOtherAvg == null || t.adminOtherAvg == null ? null : av.adminOtherAvg - t.adminOtherAvg,
        ready: av.readyAvg == null || t.readyAvg == null ? null : av.readyAvg - t.readyAvg,
      };
    }
    return { perWorker, teamAvg };
  }, [workers, workerTeams, segmentsByWorker, view.startMs, view.endMs, isAdminCF, isAdminOther, isCall, isShortBreak]);

  // Memoize per-queue grouping and packing; apply special filter for 'Everyone' queue when team/worker filter is active
  const queueData = useMemo(() => {
    const byQueue = new Map();
    for (const seg of filteredTaskSegments || []) {
      const key = seg.queue || '—';
      if (!byQueue.has(key)) byQueue.set(key, []);
      byQueue.get(key).push(seg);
    }
    // If filters are active, narrow 'Everyone' entries to handled by selected team/worker(s)
    const isFilterActive = !!(teamFilter || singleWorker || (selectedWorkers && selectedWorkers.length));
    if (isFilterActive) {
      const allKey = Array.from(byQueue.keys()).find(k => (k || '').trim().toLowerCase() === 'everyone');
      if (allKey) {
        const segs = byQueue.get(allKey) || [];
        const selectedSet = new Set(selectedWorkers || []);
        const filtered = segs.filter(seg => {
          // Only keep segments that were actually handled by someone in scope
          if (!seg.acceptedBy) return false;
          // match single worker
          if (singleWorker && seg.acceptedBy === singleWorker) return true;
          // match multi-select workers
          if (selectedSet.size && selectedSet.has(seg.acceptedBy)) return true;
          // match team: if we have workerTeams mapping
          if (teamFilter && workerTeams && workerTeams[seg.acceptedBy] === teamFilter) return true;
          return false;
        });
        byQueue.set(allKey, filtered);
      }
    }
    // If stack mode is enabled, combine all queues into one lane, still respecting allowedQueues
    let finalByQueue = byQueue;
    if (stackCalls) {
      const isEveryone = (q) => (q || '').trim().toLowerCase() === 'everyone';
      const combined = [];
      for (const [q, segs] of byQueue) {
        for (const seg of segs) {
          if (allowedQueues && allowedQueues.size) {
            // Always include outbound calls and 'Everyone' queue; otherwise require queue to be allowed
            if (!(seg?.direction === 'outbound' || isEveryone(q) || allowedQueues.has(q))) continue;
          }
          combined.push(seg);
        }
      }
      finalByQueue = new Map([["__STACK__", combined]]);
    }
    const packing = new Map();
    for (const [q, segs] of finalByQueue) packing.set(q, packIntervals(segs));
    return { byQueue: finalByQueue, packing };
  }, [filteredTaskSegments, teamFilter, singleWorker, selectedWorkers, workerTeams, stackCalls, allowedQueues]);

  // Queues visible in the lanes above (respect allowedQueues and filtered 'Everyone')
  const visibleQueues = useMemo(() => {
    const byQueue = queueData?.byQueue || new Map();
    const isEveryone = (q) => (q || '').trim().toLowerCase() === 'everyone';
    if (stackCalls) {
      // Derive actual queues represented inside the stacked lane
      const allSegs = Array.from(byQueue.values()).flat();
      const set = new Set();
      for (const seg of allSegs) {
        const q = seg.queue || '—';
        if (allowedQueues && allowedQueues.size && !(allowedQueues.has(q) || isEveryone(q))) continue;
        set.add(q);
      }
      return set;
    }
    let queues = Array.from(byQueue.keys()).filter(q => (byQueue.get(q) || []).length > 0).sort((a,b)=>a.localeCompare(b));
    if (allowedQueues && allowedQueues.size) {
      queues = queues.filter(q => allowedQueues.has(q) || isEveryone(q));
    }
    return new Set(queues);
  }, [queueData, allowedQueues, stackCalls]);

  // Inbound calls summary by Service Group (Queue) and Customer within the current view
  const inboundCallSummaries = useMemo(() => {
    const startMs = view.startMs, endMs = view.endMs;
    // Filter to inbound calls (exclude mecca and explicit outbound)
    const inbound = (filteredTaskSegments || []).filter(seg => (
      (seg.category !== 'mecca') && (seg.direction !== 'outbound')
    ));
    // Consider calls that OVERLAP the current view window and belong to visible queues
    const considered = inbound.filter(seg => {
      const s = seg.start?.toMillis?.() ?? 0;
      const e = seg.end?.toMillis?.() ?? 0;
      const overlaps = e > startMs && s < endMs;
      const inVisibleQueue = visibleQueues.size === 0 ? true : visibleQueues.has(seg.queue || '—');
      return overlaps && inVisibleQueue;
    });

    const initRec = () => ({
      total: 0,
      handled: 0,
      canceled: 0,
      rejected: 0,
      missed: 0,
      queueTimeSumSec: 0,
      queueTimeCount: 0,
      talkTimeSumSec: 0,
      talkTimeCount: 0,
    });

    const byQueue = new Map();
    const byCustomer = new Map();

    const add = (map, key, seg) => {
      const k = key || '—';
      if (!map.has(k)) map.set(k, initRec());
      const rec = map.get(k);
      rec.total += 1;
      if (seg.status === 'handled') rec.handled += 1;
      else if (seg.status === 'canceled') rec.canceled += 1;
      // Count rejected/missed as event occurrences within the view window, not final status
      const rejCount = (seg.rejections || []).filter(r => {
        const t = r.at?.toMillis?.() ?? -Infinity;
        return t > startMs && t < endMs;
      }).length;
      const misCount = (seg.timeouts || []).filter(r => {
        const t = r.at?.toMillis?.() ?? -Infinity;
        return t > startMs && t < endMs;
      }).length;
      rec.rejected += (rejCount > 0 ? 1 : 0);
      rec.missed += (misCount > 0 ? 1 : 0);
      // Queue time: from start to acceptedAt if present, otherwise until end
      const sMs = seg.start?.toMillis?.() ?? 0;
      const endMsSeg = seg.end?.toMillis?.() ?? sMs;
      const accMs = seg.acceptedAt?.toMillis?.() ?? null;
      const qSec = Math.max(0, Math.round(((accMs ?? endMsSeg) - sMs) / 1000));
      rec.queueTimeSumSec += qSec;
      rec.queueTimeCount += 1;
      // Talk time: only when handled and acceptedAt exists
      if (seg.status === 'handled' && accMs != null) {
        const tSec = Math.max(0, Math.round((endMsSeg - accMs) / 1000));
        rec.talkTimeSumSec += tSec;
        rec.talkTimeCount += 1;
      }
    };

    for (const seg of considered) {
      add(byQueue, seg.queue, seg);
      const cust = (seg.customerName && String(seg.customerName).trim()) || '—';
      add(byCustomer, cust, seg);
    }

    const toRows = (map) => Array.from(map.entries()).map(([name, r]) => ({
      name,
      total: r.total,
      handledPct: r.total ? (r.handled / r.total) : 0,
      canceledPct: r.total ? (r.canceled / r.total) : 0,
      avgQueueSec: r.queueTimeCount ? (r.queueTimeSumSec / r.queueTimeCount) : null,
      avgTalkSec: r.talkTimeCount ? (r.talkTimeSumSec / r.talkTimeCount) : null,
      handled: r.handled,
      canceled: r.canceled,
      rejected: r.rejected,
      missed: r.missed,
    }))
    .sort((a,b) => b.total - a.total);

    return {
      byQueue: toRows(byQueue),
      byCustomer: toRows(byCustomer),
    };
  }, [filteredTaskSegments, view.startMs, view.endMs, visibleQueues]);

  // Initialize/reset view domain on events change
  useEffect(() => {
    if (!events || events.length === 0) return;
    const updates = events.filter(e => e.eventType === 'worker.activity.update');
    if (updates.length === 0) return;
    const times = updates.map(e => DateTime.fromISO(e.timestamp)).filter(t => t.isValid);
    const minTime = DateTime.min(...times);
    const maxTime = DateTime.max(...times);
    const startMs = minTime.toMillis();
    const endMs = maxTime.plus({ minutes: 1 }).toMillis();
    setDomain({ startMs, endMs });
    setView({ startMs, endMs });
  }, [events]);

  return (
    // Root wrapper hosting shared tooltip; each timeline below is in its own ChartContainer
    <div
      ref={rootRef}
      style={{ display: 'flex', flexDirection: 'column', gap: 12, position: 'relative' }}
      onMouseLeave={() => { setHoverLegend(null); setHoverLabel(null); }}
      onPointerLeave={() => { setHoverLegend(null); setHoverLabel(null); }}
    >
        {/* Activity legend moved into Agent container (below) */}

        {/* Tasks/Calls per-queue lanes in its own container */}
        {taskSegments.length > 0 && (
          <ChartContainer size="large" width="100%" height={height}>
            {(() => {
          let queues = Array.from(queueData.byQueue.keys());
          const isEveryone = (q) => (q || '').trim().toLowerCase() === 'everyone';
          if (!stackCalls) {
            queues.sort((a,b)=>a.localeCompare(b));
            if (allowedQueues && allowedQueues.size) {
              queues = queues.filter(q => allowedQueues.has(q) || isEveryone(q));
            }
            // Always place 'Everyone' at the top if present
            const everyoneIdx = queues.findIndex(isEveryone);
            if (everyoneIdx > 0) {
              const [everyone] = queues.splice(everyoneIdx, 1);
              queues.unshift(everyone);
            }
          } else {
            // In stacked mode, render a single combined lane regardless of allowedQueues
            queues = ['__STACK__'];
          }
          return (
            <div
              style={{ overflowX: 'auto', overflowY: 'visible', border: '1px solid #e5e7eb', borderRadius: 8, width: '100%', position: 'relative' }}
              ref={tasksContainerRef}
              onMouseEnter={() => setHoverLegend(null)}
              onScroll={() => { setHoverLegend(null); setHoverLabel(null); }}
              onMouseDown={(e) => {
                // establish drag start relative to columns area for tasks lanes
                const c = tasksContainerRef.current || e.currentTarget;
                if (!c) return;
                const rect = c.getBoundingClientRect();
                const columnsLeft = rect.left + LEFT_COL_PX;
                const columnsRight = rect.right;
                const x = Math.min(Math.max(e.clientX, columnsLeft), columnsRight);
                setDragTasks({ active: true, startX: x, currentX: x });
              }}
              onMouseMove={(e) => {
                if (!dragTasks.active) return;
                e.preventDefault();
                const c = tasksContainerRef.current || e.currentTarget;
                if (!c) return;
                const rect = c.getBoundingClientRect();
                const columnsLeft = rect.left + LEFT_COL_PX;
                const columnsRight = rect.right;
                const x = Math.min(Math.max(e.clientX, columnsLeft), columnsRight);
                setDragTasks(d => ({ ...d, currentX: x }));
              }}
              onMouseUp={(e) => {
                if (!dragTasks.active) return;
                e.preventDefault();
                const c = tasksContainerRef.current || e.currentTarget;
                if (!c) { setDragTasks({ active: false, startX: 0, currentX: 0 }); return; }
                const rect = c.getBoundingClientRect();
                const columnsLeft = rect.left + LEFT_COL_PX;
                const columnsWidth = Math.max(1, rect.width - LEFT_COL_PX);
                const x1 = Math.min(dragTasks.startX, dragTasks.currentX);
                const x2 = Math.max(dragTasks.startX, dragTasks.currentX);
                const selPx = x2 - x1;
                setDragTasks({ active: false, startX: 0, currentX: 0 });
                if (selPx < 5) return; // ignore tiny drags
                const selStartPct = (x1 - columnsLeft) / columnsWidth;
                const selEndPct = (x2 - columnsLeft) / columnsWidth;
                const newStart = view.startMs + selStartPct * (view.endMs - view.startMs);
                const newEnd = view.startMs + selEndPct * (view.endMs - view.startMs);
                if (newEnd - newStart < 1000) return;
                setView({ startMs: Math.max(domain.startMs, newStart), endMs: Math.min(domain.endMs, newEnd) });
              }}
              onMouseLeave={() => { if (dragTasks.active) setDragTasks({ active: false, startX: 0, currentX: 0 }); }}
            >
              {/* Status legend above the queue timeline */}
              <div style={{ padding: '6px 8px', display: 'flex', justifyContent: 'flex-start' }}>
                <StatusLegend compact />
              </div>
              <div
                ref={tasksGridRef}
                style={{ display: 'grid', gridTemplateColumns: `180px repeat(${ticks.length}, minmax(6px, 1fr))`, width: '100%', position: 'relative' }}
                onMouseEnter={() => setHoverLegend(null)}
              >
                {/* header row */}
                <div style={{ position: 'sticky', left: 0, top: 0, background: '#f9fafb', zIndex: 2, borderBottom: '1px solid #eee', padding: '8px 12px', fontWeight: 600 }}>{stackCalls ? 'Calls (gestapeld)' : 'Queue'}</div>
                {ticks.map((b, i) => (
                  <div
                    key={`qt-${i}`}
                    style={{ position: 'sticky', top: 0, borderBottom: '1px solid #e5e7eb', background: '#f3f4f6', zIndex: 2, padding: '4px 6px' }}
                  />
                ))}
                {/* Header line overlay aligned with tick grid lines */}
                <div aria-hidden style={{ position: 'absolute', left: `${LEFT_COL_PX}px`, right: 0, top: 0, height: 28, pointerEvents: 'none', zIndex: 2 }}>
                  {ticks.map((tk, i) => {
                    const posPct = Math.max(0, Math.min(100, ((tk.toMillis() - tStartMs) / tSpanMs) * 100));
                    const isHour = tk.minute === 0;
                    return (
                      <div key={`qt-headline-${i}`} style={{ position: 'absolute', left: `${posPct}%`, top: 0, bottom: 0, width: isHour ? 2 : 1, background: isHour ? '#cbd5e1' : '#e9edf3', opacity: isHour ? 0.7 : 0.45 }} />
                    );
                  })}
                </div>
                {/* Header label overlay centered on lines (click to center, double-click to zoom) */}
                <div style={{ position: 'absolute', left: `${LEFT_COL_PX}px`, right: 0, top: 0, height: 28, pointerEvents: 'none', zIndex: 3 }}>
                  {ticks.map((b, i) => {
                    const isHour = b.minute === 0;
                    const label = b.toFormat('HH:mm');
                    const posPct = Math.max(0, Math.min(100, ((b.toMillis() - tStartMs) / tSpanMs) * 100));
                    const onClick = (e) => {
                      e.stopPropagation();
                      const span = Math.max(1000, view.endMs - view.startMs);
                      const center = b.toMillis();
                      let newStart = Math.max(domain.startMs, Math.min(center - span / 2, domain.endMs - span));
                      let newEnd = newStart + span;
                      setView({ startMs: newStart, endMs: newEnd });
                    };
                    const onDoubleClick = (e) => {
                      e.stopPropagation();
                      const span = Math.max(1000, view.endMs - view.startMs);
                      const newSpan = Math.max(60 * 1000, Math.round(span / 2));
                      const center = b.toMillis();
                      let newStart = Math.max(domain.startMs, Math.min(center - newSpan / 2, domain.endMs - newSpan));
                      let newEnd = newStart + newSpan;
                      setView({ startMs: newStart, endMs: newEnd });
                    };
                    return (
                      <div
                        key={`qt-label-${i}`}
                        onClick={onClick}
                        onDoubleClick={onDoubleClick}
                        title="Click to center here • Double-click to zoom in"
                        style={{ position: 'absolute', left: `${posPct}%`, top: 2, transform: 'translateX(-50%)', fontSize: isHour ? 11 : 10, fontWeight: isHour ? 700 : 600, color: '#374151', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto' }}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>
                {/* rows per queue with zebra background */}
                {queues.map((qName, rowIdx) => (
                  <React.Fragment key={`row-${qName}`}>
                    {(() => {
                      const rowBg = rowIdx % 2 === 0 ? '#fff' : '#fbfbfc';
                      return (
                        <div style={{ position: 'sticky', left: 0, background: rowBg, zIndex: 1, borderRight: '1px solid #eee', padding: '6px 12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={stackCalls && qName === '__STACK__' ? 'Alle calls' : qName}>
                          <div style={{ fontWeight: 500 }}>{stackCalls && qName === '__STACK__' ? 'Alle calls' : qName}</div>
                        </div>
                      );
                    })()}
                      {(() => {
                        const BAR_H = 12; // thin bar per row
                        const GAP = 3;
                        const rowBg = rowIdx % 2 === 0 ? '#fff' : '#fbfbfc';

                        if (stackCalls && qName === '__STACK__') {
                          // Split into top (non-abandoned) and bottom (abandoned)
                          const allSegs = queueData.byQueue.get(qName) || [];
                          const topSegs = allSegs.filter(s => s.status !== 'canceled');
                          const botSegs = allSegs.filter(s => s.status === 'canceled');
                          const packedTop = packIntervals(topSegs);
                          const packedBot = packIntervals(botSegs);
                          const topH = packedTop.rows * (BAR_H + GAP) + (packedTop.rows ? GAP : 0);
                          const sepH = packedTop.rows && packedBot.rows ? 8 : 0; // space + line area
                          const botH = packedBot.rows * (BAR_H + GAP) + (packedBot.rows ? GAP : 0);
                          const containerH = Math.max(18, topH + sepH + botH);
                          const offsetTop = 0;
                          const offsetBot = topH + sepH;
                          return (
                            <div style={{ gridColumn: `2 / span ${ticks.length}`, position: 'relative', height: containerH, background: rowBg }}>
                              {/* Top group: handled/accepted/other (non-abandoned) */}
                              {packedTop.placed.map((seg, idx) => {
                                const segStartMs = Math.max(view.startMs, seg.start.toMillis());
                                const segEndMs = Math.min(view.endMs, seg.end.toMillis());
                                if (segEndMs <= segStartMs) return null;
                                const startPct = Math.max(0, Math.min(100, ((segStartMs - tStartMs) / tSpanMs) * 100));
                                const endPct = Math.max(0, Math.min(100, ((segEndMs - tStartMs) / tSpanMs) * 100));
                                const widthPct = Math.max(0.2, endPct - startPct);
                                const palette = getPaletteForSeg(seg);
                                const color = palette[seg.status] || '#9ca3af';
                                const hasRejected = (seg.rejections || []).length > 0;
                                const hasMissed = (seg.timeouts || []).length > 0;
                                const hasQueued = !!((seg.acceptedAt && seg.start && seg.start < seg.acceptedAt) || (!seg.acceptedAt && seg.start));
                                const includeByLegend = hoverLegend ? (
                                  hoverLegend.category === (seg.category || 'call') && (
                                    (hoverLegend.status === 'rejected' && (seg.status === 'rejected' || hasRejected)) ||
                                    (hoverLegend.status === 'missed' && (seg.status === 'missed' || hasMissed)) ||
                                    (hoverLegend.status === 'outbound' && seg.direction === 'outbound') ||
                                    (['handled','accepted','canceled','unknown'].includes(hoverLegend.status) && seg.status === hoverLegend.status)
                                  )
                                ) : true;
                                let dimmedByWorkerHover = false;
                                let workerOverlapHighlight = false;
                                if (hoverWorkerTarget) {
                                  const overlapsWorker = seg.end.toMillis() > hoverWorkerTarget.startMs && seg.start.toMillis() < hoverWorkerTarget.endMs;
                                  const involved = (seg.acceptedBy && seg.acceptedBy === hoverWorkerTarget.worker)
                                    || (seg.rejectedBy || []).includes(hoverWorkerTarget.worker)
                                    || (seg.missedBy || []).includes(hoverWorkerTarget.worker);
                                  workerOverlapHighlight = overlapsWorker && involved;
                                  dimmedByWorkerHover = !workerOverlapHighlight;
                                }
                                const thisSegKey = `${seg.sid}-${seg.start.toMillis?.() ?? ''}-${seg.end.toMillis?.() ?? ''}`;
                                let dimmed = false;
                                if (hoverTaskSegKey) {
                                  dimmed = hoverTaskSegKey !== thisSegKey;
                                } else if (hoverLegend) {
                                  dimmed = !includeByLegend;
                                } else {
                                  dimmed = dimmedByWorkerHover;
                                }
                                return (
                                  <div
                                    key={`task-top-${seg.sid}-${idx}`}
                                    style={{ position: 'absolute', left: `${startPct}%`, width: `${widthPct}%`, top: offsetTop + GAP + seg._row * (BAR_H + GAP), height: BAR_H, background: color, borderRadius: 3, border: workerOverlapHighlight ? '2px solid rgba(0,0,0,0.45)' : '1px solid rgba(0,0,0,0.25)', boxShadow: workerOverlapHighlight ? '0 0 0 2px rgba(59,130,246,0.45)' : 'none', cursor: onTaskClick ? 'pointer' : 'default', opacity: dimmed ? 0.35 : 1, zIndex: workerOverlapHighlight ? 2 : 1 }}
                                    onMouseEnter={(e) => {
                                      if (hoverWorkerTarget) setHoverWorkerTarget(null);
                                      if (hoverLegend) setHoverLegend(null);
                                      setHoverTaskSegKey(thisSegKey);
                                      const durSec = Math.max(1, Math.round((seg.end.toMillis() - seg.start.toMillis()) / 1000));
                                      const durStr = durSec < 60 ? `${durSec}s` : `${Math.floor(durSec/60)}m ${durSec%60}s`;
                                      const pos = placeTooltip(e.clientX, e.clientY);
                                      const who = seg.acceptedBy ? `Handled by: ${seg.acceptedBy}` : seg.rejectedBy.length ? `Rejected by: ${seg.rejectedBy.join(', ')}` : seg.missedBy.length ? `Missed by: ${seg.missedBy.join(', ')}` : '—';
                                      const rel = new Set();
                                      const roles = new Map();
                                      const pal = getPaletteForSeg(seg);
                                      if (seg.acceptedBy) { rel.add(seg.acceptedBy); roles.set(seg.acceptedBy, { role: 'handled', color: pal.handled }); }
                                      (seg.rejectedBy || []).forEach(w => { if (!w) return; rel.add(w); if (!roles.has(w)) roles.set(w, { role: 'rejected', color: pal.rejected }); });
                                      (seg.missedBy || []).forEach(w => { if (!w) return; rel.add(w); if (!roles.has(w)) roles.set(w, { role: 'missed', color: pal.missed }); });
                                      setHoverTaskWorkers(rel.size ? rel : null);
                                      setHoverTaskWorkerRoles(roles.size ? roles : null);
                                      setHoverTaskWindow({ startMs: seg.start.toMillis(), endMs: seg.end.toMillis() });
                                      const rejStrs = (seg.rejections || []).map(r => `${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`);
                                      const misStrs = (seg.timeouts || []).map(r => `${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`);
                                      const details = [];
                                      if (rejStrs.length) details.push(`Rejections: ${rejStrs.join(' · ')}`);
                                      if (misStrs.length) details.push(`Missed: ${misStrs.join(' · ')}`);
                                      if (seg.acceptedAt) {
                                        const qSec = Math.max(0, Math.round((seg.acceptedAt.toMillis() - seg.start.toMillis()) / 1000));
                                        details.unshift(`In queue: ${qSec < 60 ? `${qSec}s` : `${Math.floor(qSec/60)}m ${qSec%60}s`}`);
                                      }
                                      if (seg.acceptedAt && seg.wrapupAt) {
                                        const callSec = Math.max(0, Math.round((seg.wrapupAt.toMillis() - seg.acceptedAt.toMillis()) / 1000));
                                        details.push(`Call: ${callSec < 60 ? `${callSec}s` : `${Math.floor(callSec/60)}m ${callSec%60}s`}`);
                                      }
                                      if (seg.status === 'handled' && seg.wrapupAt) {
                                        const wuSec = Math.max(0, Math.round((seg.end.toMillis() - seg.wrapupAt.toMillis()) / 1000));
                                        details.push(`Wrap-up: ${wuSec < 60 ? `${wuSec}s` : `${Math.floor(wuSec/60)}m ${wuSec%60}s`}`);
                                      }
                                      setTooltip({ visible: true, x: pos.x, y: pos.y, lines: [ [seg.type, seg.direction].filter(Boolean).join(' · '), (seg.category === 'mecca' && seg.taskSubject) ? `Subject: ${seg.taskSubject}` : null, seg.customerName ? `Customer: ${seg.customerName}` : null, `From: ${seg.from || '—'} · To: ${seg.to || '—'}`, who, `Start: ${seg.start.toFormat('HH:mm:ss')}`, `Einde: ${seg.end.toFormat('HH:mm:ss')}`, `Duur: ${durStr}`, ...details ].filter(Boolean) });
                                    }}
                                    onMouseMove={(e) => moveTooltip(e.clientX, e.clientY)}
                                    onMouseLeave={() => { setTooltip(t => ({ ...t, visible: false })); setHoverTaskWorkers(null); setHoverTaskWorkerRoles(null); setHoverTaskWindow(null); setHoverTaskSegKey(null); }}
                                    onClick={() => onTaskClick && onTaskClick(seg.sid)}
                                  >
                                    {/* Skip in-queue overlay when zoomed out for performance */}
                                    {!LOD.coarse && seg.category !== 'mecca' && seg.status !== 'canceled' && (() => {
                                      const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                                      const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                                      const segViewDur = Math.max(1, segViewEnd - segViewStart);
                                      const qStartMs = segViewStart;
                                      const accMsRaw = seg.acceptedAt ? seg.acceptedAt.toMillis() : null;
                                      const qEndMs = Math.min(segViewEnd, accMsRaw ?? segViewEnd);
                                      if (qEndMs <= qStartMs) return null;
                                      const leftWithinSegPct = 0;
                                      const widthWithinSegPct = Math.max(0, ((qEndMs - qStartMs) / segViewDur) * 100);
                                      const overlayStyle = { position: 'absolute', left: `${leftWithinSegPct}%`, top: 0, height: '100%', width: `${widthWithinSegPct}%`, background: inQueueOverlayFor(seg.category || 'call'), borderTopLeftRadius: 3, borderBottomLeftRadius: 3, pointerEvents: 'none', zIndex: 2, borderRight: seg.acceptedAt ? '2px solid rgba(0,0,0,0.35)' : 'none' };
                                      return <div style={overlayStyle} />;
                                    })()}
                                    {/* Wrap-up overlay: from wrapupAt to end for handled tasks */}
                                    {!LOD.coarse && seg.category !== 'mecca' && seg.status === 'handled' && seg.wrapupAt && (() => {
                                      const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                                      const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                                      const segViewDur = Math.max(1, segViewEnd - segViewStart);
                                      const wuStartMs = Math.max(segViewStart, seg.wrapupAt.toMillis());
                                      const wuEndMs = segViewEnd;
                                      if (wuEndMs <= wuStartMs) return null;
                                      const leftWithinSegPct = Math.max(0, ((wuStartMs - segViewStart) / segViewDur) * 100);
                                      const widthWithinSegPct = Math.max(0, ((wuEndMs - wuStartMs) / segViewDur) * 100);
                                      const overlayStyle = { position: 'absolute', left: `${leftWithinSegPct}%`, top: 0, height: '100%', width: `${widthWithinSegPct}%`, background: 'rgba(247, 144, 9, 0.28)', borderTopRightRadius: 3, borderBottomRightRadius: 3, pointerEvents: 'none', zIndex: 1, borderLeft: '2px dashed rgba(0,0,0,0.25)' };
                                      return <div style={overlayStyle} title="Wrap-up" />;
                                    })()}
                                    {/* Skip fine markers when zoomed out */}
                                    {!LOD.coarse && (seg.rejections || []).map((r, ri) => {
                                      const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                                      const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                                      const segViewDur = Math.max(1, segViewEnd - segViewStart);
                                      const tMs = r.at.toMillis();
                                      const clamped = Math.max(segViewStart, Math.min(segViewEnd, tMs));
                                      const pctWithinSeg = Math.max(0, Math.min(100, ((clamped - segViewStart) / segViewDur) * 100));
                                      return <CrossIcon key={`rej-top-${ri}`} color={palette.rejected} size={14} stroke={3} style={{ position: 'absolute', left: `${pctWithinSeg}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }} title={`Rejected by ${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`} />;
                                    })}
                                    {!LOD.coarse && (seg.timeouts || []).map((r, ri) => {
                                      const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                                      const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                                      const segViewDur = Math.max(1, segViewEnd - segViewStart);
                                      const tMs = r.at.toMillis();
                                      const clamped = Math.max(segViewStart, Math.min(segViewEnd, tMs));
                                      const pctWithinSeg = Math.max(0, Math.min(100, ((clamped - segViewStart) / segViewDur) * 100));
                                      return <CrossIcon key={`mis-top-${ri}`} color={palette.missed} size={14} stroke={3} style={{ position: 'absolute', left: `${pctWithinSeg}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }} title={`Missed by ${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`} />;
                                    })}
                                  </div>
                                );
                              })}

                              {/* Separator line between top and bottom when both exist */}
                              {(packedTop.rows && packedBot.rows) && (
                                <div style={{ position: 'absolute', left: 0, right: 0, top: topH + Math.floor(sepH/2), height: 1, background: '#e5e7eb', opacity: 0.9 }} />
                              )}

                              {/* Bottom group: abandoned (canceled) */}
                              {packedBot.placed.map((seg, idx) => {
                                const segStartMs = Math.max(view.startMs, seg.start.toMillis());
                                const segEndMs = Math.min(view.endMs, seg.end.toMillis());
                                if (segEndMs <= segStartMs) return null;
                                const startPct = Math.max(0, Math.min(100, ((segStartMs - tStartMs) / tSpanMs) * 100));
                                const endPct = Math.max(0, Math.min(100, ((segEndMs - tStartMs) / tSpanMs) * 100));
                                const widthPct = Math.max(0.2, endPct - startPct);
                                const palette = getPaletteForSeg(seg);
                                const color = palette[seg.status] || '#9ca3af';
                                const hasRejected = (seg.rejections || []).length > 0;
                                const hasMissed = (seg.timeouts || []).length > 0;
                                const includeByLegend = hoverLegend ? (
                                  hoverLegend.category === (seg.category || 'call') && (
                                    (hoverLegend.status === 'rejected' && (seg.status === 'rejected' || hasRejected)) ||
                                    (hoverLegend.status === 'missed' && (seg.status === 'missed' || hasMissed)) ||
                                    (hoverLegend.status === 'outbound' && seg.direction === 'outbound') ||
                                    (['handled','accepted','canceled','unknown'].includes(hoverLegend.status) && seg.status === hoverLegend.status)
                                  )
                                ) : true;
                                let dimmedByWorkerHover = false;
                                let workerOverlapHighlight = false;
                                if (hoverWorkerTarget) {
                                  const overlapsWorker = seg.end.toMillis() > hoverWorkerTarget.startMs && seg.start.toMillis() < hoverWorkerTarget.endMs;
                                  const involved = (seg.acceptedBy && seg.acceptedBy === hoverWorkerTarget.worker)
                                    || (seg.rejectedBy || []).includes(hoverWorkerTarget.worker)
                                    || (seg.missedBy || []).includes(hoverWorkerTarget.worker);
                                  workerOverlapHighlight = overlapsWorker && involved;
                                  dimmedByWorkerHover = !workerOverlapHighlight;
                                }
                                const thisSegKey = `${seg.sid}-${seg.start.toMillis?.() ?? ''}-${seg.end.toMillis?.() ?? ''}`;
                                let dimmed = false;
                                if (hoverTaskSegKey) {
                                  dimmed = hoverTaskSegKey !== thisSegKey;
                                } else if (hoverLegend) {
                                  dimmed = !includeByLegend;
                                } else {
                                  dimmed = dimmedByWorkerHover;
                                }
                                return (
                                  <div
                                    key={`task-bot-${seg.sid}-${idx}`}
                                    style={{ position: 'absolute', left: `${startPct}%`, width: `${widthPct}%`, top: offsetBot + GAP + seg._row * (BAR_H + GAP), height: BAR_H, background: color, borderRadius: 3, border: workerOverlapHighlight ? '2px solid rgba(0,0,0,0.45)' : '1px solid rgba(0,0,0,0.25)', boxShadow: workerOverlapHighlight ? '0 0 0 2px rgba(59,130,246,0.45)' : 'none', cursor: onTaskClick ? 'pointer' : 'default', opacity: dimmed ? 0.35 : 1, zIndex: workerOverlapHighlight ? 2 : 1 }}
                                    onMouseEnter={(e) => {
                                      if (hoverWorkerTarget) setHoverWorkerTarget(null);
                                      if (hoverLegend) setHoverLegend(null);
                                      setHoverTaskSegKey(thisSegKey);
                                      const durSec = Math.max(1, Math.round((seg.end.toMillis() - seg.start.toMillis()) / 1000));
                                      const durStr = durSec < 60 ? `${durSec}s` : `${Math.floor(durSec/60)}m ${durSec%60}s`;
                                      const pos = placeTooltip(e.clientX, e.clientY);
                                      const who = seg.acceptedBy ? `Handled by: ${seg.acceptedBy}` : seg.rejectedBy.length ? `Rejected by: ${seg.rejectedBy.join(', ')}` : seg.missedBy.length ? `Missed by: ${seg.missedBy.join(', ')}` : '—';
                                      const rel = new Set();
                                      const roles = new Map();
                                      const pal = getPaletteForSeg(seg);
                                      if (seg.acceptedBy) { rel.add(seg.acceptedBy); roles.set(seg.acceptedBy, { role: 'handled', color: pal.handled }); }
                                      (seg.rejectedBy || []).forEach(w => { if (!w) return; rel.add(w); if (!roles.has(w)) roles.set(w, { role: 'rejected', color: pal.rejected }); });
                                      (seg.missedBy || []).forEach(w => { if (!w) return; rel.add(w); if (!roles.has(w)) roles.set(w, { role: 'missed', color: pal.missed }); });
                                      setHoverTaskWorkers(rel.size ? rel : null);
                                      setHoverTaskWorkerRoles(roles.size ? roles : null);
                                      setHoverTaskWindow({ startMs: seg.start.toMillis(), endMs: seg.end.toMillis() });
                                      const rejStrs = (seg.rejections || []).map(r => `${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`);
                                      const misStrs = (seg.timeouts || []).map(r => `${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`);
                                      const details = [];
                                      if (rejStrs.length) details.push(`Rejections: ${rejStrs.join(' · ')}`);
                                      if (misStrs.length) details.push(`Missed: ${misStrs.join(' · ')}`);
                                      if (seg.acceptedAt) {
                                        const qSec = Math.max(0, Math.round((seg.acceptedAt.toMillis() - seg.start.toMillis()) / 1000));
                                        details.unshift(`In queue: ${qSec < 60 ? `${qSec}s` : `${Math.floor(qSec/60)}m ${qSec%60}s`}`);
                                      }
                                      if (seg.acceptedAt && seg.wrapupAt) {
                                        const callSec = Math.max(0, Math.round((seg.wrapupAt.toMillis() - seg.acceptedAt.toMillis()) / 1000));
                                        details.push(`Call: ${callSec < 60 ? `${callSec}s` : `${Math.floor(callSec/60)}m ${callSec%60}s`}`);
                                      }
                                      if (seg.status === 'handled' && seg.wrapupAt) {
                                        const wuSec = Math.max(0, Math.round((seg.end.toMillis() - seg.wrapupAt.toMillis()) / 1000));
                                        details.push(`Wrap-up: ${wuSec < 60 ? `${wuSec}s` : `${Math.floor(wuSec/60)}m ${wuSec%60}s`}`);
                                      }
                                      setTooltip({ visible: true, x: pos.x, y: pos.y, lines: [ [seg.type, seg.direction].filter(Boolean).join(' · '), (seg.category === 'mecca' && seg.taskSubject) ? `Subject: ${seg.taskSubject}` : null, seg.customerName ? `Customer: ${seg.customerName}` : null, `From: ${seg.from || '—'} · To: ${seg.to || '—'}`, who, `Start: ${seg.start.toFormat('HH:mm:ss')}`, `Einde: ${seg.end.toFormat('HH:mm:ss')}`, `Duur: ${durStr}` ].filter(Boolean) });
                                    }}
                                    onMouseMove={(e) => moveTooltip(e.clientX, e.clientY)}
                                    onMouseLeave={() => { setTooltip(t => ({ ...t, visible: false })); setHoverTaskWorkers(null); setHoverTaskWorkerRoles(null); setHoverTaskWindow(null); setHoverTaskSegKey(null); }}
                                    onClick={() => onTaskClick && onTaskClick(seg.sid)}
                                  >
                                    {/* For abandoned we don't show queue overlay since there's no acceptance boundary */}
                                    {!LOD.coarse && (seg.rejections || []).map((r, ri) => {
                                      const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                                      const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                                      const segViewDur = Math.max(1, segViewEnd - segViewStart);
                                      const tMs = r.at.toMillis();
                                      const clamped = Math.max(segViewStart, Math.min(segViewEnd, tMs));
                                      const pctWithinSeg = Math.max(0, Math.min(100, ((clamped - segViewStart) / segViewDur) * 100));
                                      return <CrossIcon key={`rej-bot-${ri}`} color={palette.rejected} size={14} stroke={3} style={{ position: 'absolute', left: `${pctWithinSeg}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }} title={`Rejected by ${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`} />;
                                    })}
                                    {!LOD.coarse && (seg.timeouts || []).map((r, ri) => {
                                      const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                                      const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                                      const segViewDur = Math.max(1, segViewEnd - segViewStart);
                                      const tMs = r.at.toMillis();
                                      const clamped = Math.max(segViewStart, Math.min(segViewEnd, tMs));
                                      const pctWithinSeg = Math.max(0, Math.min(100, ((clamped - segViewStart) / segViewDur) * 100));
                                      return <CrossIcon key={`mis-bot-${ri}`} color={palette.missed} size={14} stroke={3} style={{ position: 'absolute', left: `${pctWithinSeg}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }} title={`Missed by ${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`} />;
                                    })}
                                  </div>
                                );
                              })}

                              {/* tick guides are rendered once per grid below */}
                            </div>
                          );
                        }

                        // Default per-queue rendering (non-stacked)
                        const qPacked = queueData.packing.get(qName) || { placed: [], rows: 0 };
                        const containerH = Math.max(18, qPacked.rows * (BAR_H + GAP) + GAP);
                        return (
                          <div style={{ gridColumn: `2 / span ${ticks.length}`, position: 'relative', height: containerH, background: rowBg }}>
                            {qPacked.placed.map((seg, idx) => {
                        const segStartMs = Math.max(view.startMs, seg.start.toMillis());
                        const segEndMs = Math.min(view.endMs, seg.end.toMillis());
                        if (segEndMs <= segStartMs) return null;
                        const startPct = Math.max(0, Math.min(100, ((segStartMs - tStartMs) / tSpanMs) * 100));
                        const endPct = Math.max(0, Math.min(100, ((segEndMs - tStartMs) / tSpanMs) * 100));
                        const widthPct = Math.max(0.2, endPct - startPct);
                        const palette = getPaletteForSeg(seg);
                        const color = palette[seg.status] || '#9ca3af';
                        const hasRejected = (seg.rejections || []).length > 0;
                        const hasMissed = (seg.timeouts || []).length > 0;
                        const hasQueued = !!((seg.acceptedAt && seg.start && seg.start < seg.acceptedAt) || (!seg.acceptedAt && seg.start));
                        const includeByLegend = hoverLegend ? (
                          hoverLegend.category === (seg.category || 'call') && (
                            (hoverLegend.status === 'rejected' && (seg.status === 'rejected' || hasRejected)) ||
                            (hoverLegend.status === 'missed' && (seg.status === 'missed' || hasMissed)) ||
                            (hoverLegend.status === 'outbound' && seg.direction === 'outbound') ||
                            (['handled','accepted','canceled','unknown'].includes(hoverLegend.status) && seg.status === hoverLegend.status)
                          )
                        ) : true;
                        // Reverse hover: from worker activity to tasks
                        let dimmedByWorkerHover = false;
                        let workerOverlapHighlight = false;
                        if (hoverWorkerTarget) {
                          const overlapsWorker = seg.end.toMillis() > hoverWorkerTarget.startMs && seg.start.toMillis() < hoverWorkerTarget.endMs;
                          const involved = (seg.acceptedBy && seg.acceptedBy === hoverWorkerTarget.worker)
                            || (seg.rejectedBy || []).includes(hoverWorkerTarget.worker)
                            || (seg.missedBy || []).includes(hoverWorkerTarget.worker);
                          workerOverlapHighlight = overlapsWorker && involved;
                          dimmedByWorkerHover = !workerOverlapHighlight;
                        }
                                // Focus dimming: if a specific task bar is hovered, dim all others in the tasks table
                                const thisSegKey = `${seg.sid}-${seg.start.toMillis?.() ?? ''}-${seg.end.toMillis?.() ?? ''}`;
                                let dimmed = false;
                                if (hoverTaskSegKey) {
                                  dimmed = hoverTaskSegKey !== thisSegKey;
                                } else if (hoverLegend) {
                                  dimmed = !includeByLegend;
                                } else {
                                  dimmed = dimmedByWorkerHover;
                                }
                        return (
          <div
                            key={`task-${seg.sid}-${idx}`}
            style={{ position: 'absolute', left: `${startPct}%`, width: `${widthPct}%`, top: GAP + seg._row * (BAR_H + GAP), height: BAR_H, background: color, borderRadius: 3, border: workerOverlapHighlight ? '2px solid rgba(0,0,0,0.45)' : '1px solid rgba(0,0,0,0.25)', boxShadow: workerOverlapHighlight ? '0 0 0 2px rgba(59,130,246,0.45)' : 'none', cursor: onTaskClick ? 'pointer' : 'default', opacity: dimmed ? 0.35 : 1, zIndex: workerOverlapHighlight ? 2 : 1 }}
                            onMouseEnter={(e) => {
                              // When hovering a task bar, disable worker->task hover to avoid conflict
                              if (hoverWorkerTarget) setHoverWorkerTarget(null);
                              if (hoverLegend) setHoverLegend(null);
                                      setHoverTaskSegKey(thisSegKey);
                              const durSec = Math.max(1, Math.round((seg.end.toMillis() - seg.start.toMillis()) / 1000));
                              const durStr = durSec < 60 ? `${durSec}s` : `${Math.floor(durSec/60)}m ${durSec%60}s`;
                              const pos = placeTooltip(e.clientX, e.clientY);
                              const who = seg.acceptedBy ? `Handled by: ${seg.acceptedBy}` : seg.rejectedBy.length ? `Rejected by: ${seg.rejectedBy.join(', ')}` : seg.missedBy.length ? `Missed by: ${seg.missedBy.join(', ')}` : '—';
                              const dir = seg.direction ? ` · ${seg.direction}` : '';
                              // set cross-highlight workers and window for Agent timeline
                              const rel = new Set();
                              const roles = new Map();
                              const pal = getPaletteForSeg(seg);
                              if (seg.acceptedBy) {
                                rel.add(seg.acceptedBy);
                                roles.set(seg.acceptedBy, { role: 'handled', color: pal.handled });
                              }
                              (seg.rejectedBy || []).forEach(w => {
                                if (!w) return;
                                rel.add(w);
                                if (!roles.has(w)) roles.set(w, { role: 'rejected', color: pal.rejected });
                              });
                              (seg.missedBy || []).forEach(w => {
                                if (!w) return;
                                rel.add(w);
                                if (!roles.has(w)) roles.set(w, { role: 'missed', color: pal.missed });
                              });
                              setHoverTaskWorkers(rel.size ? rel : null);
                              setHoverTaskWorkerRoles(roles.size ? roles : null);
                              setHoverTaskWindow({ startMs: seg.start.toMillis(), endMs: seg.end.toMillis() });
                              const rejStrs = (seg.rejections || []).map(r => `${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`);
                              const misStrs = (seg.timeouts || []).map(r => `${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`);
                              const details = [];
                              if (rejStrs.length) details.push(`Rejections: ${rejStrs.join(' · ')}`);
                              if (misStrs.length) details.push(`Missed: ${misStrs.join(' · ')}`);
                              if (seg.acceptedAt) {
                                const qSec = Math.max(0, Math.round((seg.acceptedAt.toMillis() - seg.start.toMillis()) / 1000));
                                details.unshift(`In queue: ${qSec < 60 ? `${qSec}s` : `${Math.floor(qSec/60)}m ${qSec%60}s`}`);
                              }
                              if (seg.acceptedAt && seg.wrapupAt) {
                                const callSec = Math.max(0, Math.round((seg.wrapupAt.toMillis() - seg.acceptedAt.toMillis()) / 1000));
                                details.push(`Call: ${callSec < 60 ? `${callSec}s` : `${Math.floor(callSec/60)}m ${callSec%60}s`}`);
                              }
                              if (seg.status === 'handled' && seg.wrapupAt) {
                                const wuSec = Math.max(0, Math.round((seg.end.toMillis() - seg.wrapupAt.toMillis()) / 1000));
                                details.push(`Wrap-up: ${wuSec < 60 ? `${wuSec}s` : `${Math.floor(wuSec/60)}m ${wuSec%60}s`}`);
                              }
                              setTooltip({
                                visible: true,
                                x: pos.x,
                                y: pos.y,
                                lines: [
                                  [seg.type, seg.direction].filter(Boolean).join(' · '),
                                  (seg.category === 'mecca' && seg.taskSubject) ? `Subject: ${seg.taskSubject}` : null,
                                  seg.customerName ? `Customer: ${seg.customerName}` : null,
                                  `From: ${seg.from || '—'} · To: ${seg.to || '—'}`,
                                  who,
                                  `Start: ${seg.start.toFormat('HH:mm:ss')}`,
                                  `Einde: ${seg.end.toFormat('HH:mm:ss')}`,
                                  `Duur: ${durStr}`,
                                  ...details
                                ]
                              });
                            }}
                            onMouseMove={(e) => moveTooltip(e.clientX, e.clientY)}
                                    onMouseLeave={() => { setTooltip(t => ({ ...t, visible: false })); setHoverTaskWorkers(null); setHoverTaskWorkerRoles(null); setHoverTaskWindow(null); setHoverTaskSegKey(null); }}
                            onClick={() => onTaskClick && onTaskClick(seg.sid)}
                          >
                            {/* in-queue overlay from created/start to acceptedAt */}
                            {/* Skip in-queue overlay when zoomed out for performance */}
                            {!LOD.coarse && seg.category !== 'mecca' && seg.status !== 'canceled' && (() => {
                              // Determine queued range as a proportion of the segment itself (within current view)
                              const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                              const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                              const segViewDur = Math.max(1, segViewEnd - segViewStart);
                              const qStartMs = segViewStart; // queue starts at segment start within view
                              const accMsRaw = seg.acceptedAt ? seg.acceptedAt.toMillis() : null;
                              const qEndMs = Math.min(segViewEnd, accMsRaw ?? segViewEnd);
                              if (qEndMs <= qStartMs) return null;
                              const leftWithinSegPct = 0; // since qStartMs == segViewStart
                              const widthWithinSegPct = Math.max(0, ((qEndMs - qStartMs) / segViewDur) * 100);
                              const overlayStyle = {
                                position: 'absolute',
                                left: `${leftWithinSegPct}%`,
                                top: 0,
                                height: '100%',
                                width: `${widthWithinSegPct}%`,
                                background: inQueueOverlayFor(seg.category || 'call'),
                                borderTopLeftRadius: 3,
                                borderBottomLeftRadius: 3,
                                pointerEvents: 'none',
                                zIndex: 2,
                                borderRight: seg.acceptedAt ? '2px solid rgba(0,0,0,0.35)' : 'none'
                              };
                              return <div style={overlayStyle} />;
                            })()}
                            {/* Wrap-up overlay: from wrapupAt to end for handled tasks */}
                            {!LOD.coarse && seg.category !== 'mecca' && seg.status === 'handled' && seg.wrapupAt && (() => {
                              const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                              const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                              const segViewDur = Math.max(1, segViewEnd - segViewStart);
                              const wuStartMs = Math.max(segViewStart, seg.wrapupAt.toMillis());
                              const wuEndMs = segViewEnd;
                              if (wuEndMs <= wuStartMs) return null;
                              const leftWithinSegPct = Math.max(0, ((wuStartMs - segViewStart) / segViewDur) * 100);
                              const widthWithinSegPct = Math.max(0, ((wuEndMs - wuStartMs) / segViewDur) * 100);
                              const overlayStyle = { position: 'absolute', left: `${leftWithinSegPct}%`, top: 0, height: '100%', width: `${widthWithinSegPct}%`, background: 'rgba(247, 144, 9, 0.28)', borderTopRightRadius: 3, borderBottomRightRadius: 3, pointerEvents: 'none', zIndex: 1, borderLeft: '2px dashed rgba(0,0,0,0.25)' };
                              return <div style={overlayStyle} title="Wrap-up" />;
                            })()}
                            {/* markers for rejections and missed timeouts */}
                            {/* Skip fine markers when zoomed out */}
                            {!LOD.coarse && (seg.rejections || []).map((r, ri) => {
                              const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                              const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                              const segViewDur = Math.max(1, segViewEnd - segViewStart);
                              const tMs = r.at.toMillis();
                              const clamped = Math.max(segViewStart, Math.min(segViewEnd, tMs));
                              const pctWithinSeg = Math.max(0, Math.min(100, ((clamped - segViewStart) / segViewDur) * 100));
                              return (
                                <CrossIcon
                                  key={`rej-${ri}`}
                                  color={palette.rejected}
                                  size={14}
                                  stroke={3}
                                  style={{ position: 'absolute', left: `${pctWithinSeg}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }}
                                  title={`Rejected by ${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`}
                                />
                              );
                            })}
                            {!LOD.coarse && (seg.timeouts || []).map((r, ri) => {
                              const segViewStart = Math.max(view.startMs, seg.start.toMillis());
                              const segViewEnd = Math.min(view.endMs, seg.end.toMillis());
                              const segViewDur = Math.max(1, segViewEnd - segViewStart);
                              const tMs = r.at.toMillis();
                              const clamped = Math.max(segViewStart, Math.min(segViewEnd, tMs));
                              const pctWithinSeg = Math.max(0, Math.min(100, ((clamped - segViewStart) / segViewDur) * 100));
                              return (
                                <CrossIcon
                                  key={`mis-${ri}`}
                                  color={palette.missed}
                                  size={14}
                                  stroke={3}
                                  style={{ position: 'absolute', left: `${pctWithinSeg}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }}
                                  title={`Missed by ${r.worker || '—'} @ ${r.at.toFormat('HH:mm:ss')}`}
                                />
                              );
                            })}
                          </div>
                        );
                            })}
                            {/* tick guides are rendered once per grid below */}
                          </div>
                        );
                      })()}
                  </React.Fragment>
                ))}
                {/* One-time tick overlay for the entire tasks grid */}
                <div aria-hidden style={{ position: 'absolute', left: `${LEFT_COL_PX}px`, right: 0, top: 0, bottom: 0, pointerEvents: 'none', zIndex: 0 }}>
                  {ticks.map((tk, i) => {
                    const posPct = Math.max(0, Math.min(100, ((tk.toMillis() - tStartMs) / tSpanMs) * 100));
                    const isHour = tk.minute === 0;
                    return (
                      <div key={`qline-grid-${i}`} style={{ position: 'absolute', left: `${posPct}%`, top: 0, bottom: 0, width: isHour ? 2 : 1, background: isHour ? '#cbd5e1' : '#e9edf3', opacity: isHour ? 0.7 : 0.45 }} />
                    );
                  })}
                </div>
              </div>
              {/* Drag selection overlay for tasks lanes */}
              {dragTasks.active && tasksContainerRef.current && (() => {
                const rect = tasksContainerRef.current.getBoundingClientRect();
                const columnsLeft = rect.left + LEFT_COL_PX;
                const leftPx = Math.min(dragTasks.startX, dragTasks.currentX) - columnsLeft;
                const widthPx = Math.abs(dragTasks.currentX - dragTasks.startX);
                const scrollLeft = tasksContainerRef.current.scrollLeft || 0;
                const topOffset = tasksGridRef.current ? tasksGridRef.current.offsetTop : 40;
                return (
                  <div style={{ position: 'absolute', left: scrollLeft + LEFT_COL_PX + leftPx, top: topOffset, height: `calc(100% - ${topOffset}px)`, width: widthPx, background: 'rgba(5,127,162,0.15)', border: '1px solid rgba(5,127,162,0.45)', pointerEvents: 'none' }} />
                );
              })()}
            </div>
          );
            })()}
          </ChartContainer>
        )}
        {/* Agent timeline in its own container */}
        <ChartContainer size="large" width="100%" height={height}>
          <div
          style={{ overflowX: 'auto', overflowY: 'visible', border: '1px solid #e5e7eb', borderRadius: 8, width: '100%', position: 'relative' }}
            onScroll={() => { setHoverLegend(null); setHoverLabel(null); }}
          onMouseDown={(e) => {
            e.preventDefault();
            // establish drag start relative to columns area
            const container = containerRef.current || e.currentTarget;
            const rect = container.getBoundingClientRect();
            const columnsLeft = rect.left + LEFT_COL_PX;
            const columnsRight = rect.right;
            const x = Math.min(Math.max(e.clientX, columnsLeft), columnsRight);
            setDrag({ active: true, startX: x, currentX: x });
          }}
          onMouseMove={(e) => {
            if (!drag.active) return;
            e.preventDefault();
            const container = containerRef.current || e.currentTarget;
            const rect = container.getBoundingClientRect();
            const columnsLeft = rect.left + LEFT_COL_PX;
            const columnsRight = rect.right;
            const x = Math.min(Math.max(e.clientX, columnsLeft), columnsRight);
            setDrag(d => ({ ...d, currentX: x }));
          }}
          onMouseUp={(e) => {
            if (!drag.active) return;
            e.preventDefault();
            const container = containerRef.current || e.currentTarget;
            const rect = container.getBoundingClientRect();
            const columnsLeft = rect.left + LEFT_COL_PX;
            const columnsWidth = Math.max(1, rect.width - LEFT_COL_PX);
            const x1 = Math.min(drag.startX, drag.currentX);
            const x2 = Math.max(drag.startX, drag.currentX);
            const selPx = x2 - x1;
            setDrag({ active: false, startX: 0, currentX: 0 });
            if (selPx < 5) return; // ignore tiny drags
            const selStartPct = (x1 - columnsLeft) / columnsWidth;
            const selEndPct = (x2 - columnsLeft) / columnsWidth;
            const newStart = view.startMs + selStartPct * (view.endMs - view.startMs);
            const newEnd = view.startMs + selEndPct * (view.endMs - view.startMs);
            // prevent too-small window
            if (newEnd - newStart < 1000) return;
            setView({ startMs: Math.max(domain.startMs, newStart), endMs: Math.min(domain.endMs, newEnd) });
          }}
          onMouseLeave={() => { if (drag.active) setDrag({ active: false, startX: 0, currentX: 0 }); }}
          ref={containerRef}
          >
          {/* Activities legend inside Agent container */}
          <div
            style={{ padding: '6px 10px', borderBottom: '1px solid #eee', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
            onMouseLeave={() => setHoverLabel(null)}
            onPointerLeave={() => setHoverLabel(null)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {Array.from(labels).sort((a,b)=>a.localeCompare(b)).map(lbl => (
                <span
                  key={lbl}
                  onMouseEnter={() => setHoverLabel(lbl)}
                  onMouseLeave={() => setHoverLabel(null)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: hoverLabel && hoverLabel !== lbl ? 0.4 : 1, fontSize: 12, padding: '3px 6px', margin: '2px 3px', borderRadius: 4 }}
                >
                  <span style={{ width: 12, height: 12, background: stringToColor(lbl), display: 'inline-block', border: '1px solid #e5e7eb' }} />
                  {lbl}
                </span>
              ))}
            </div>
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: `180px repeat(${ticks.length}, minmax(6px, 1fr))`, width: '100%', position: 'relative' }}
            onMouseEnter={() => setHoverLabel(null)}
          >
            {/* header row (sticky for page scroll) */}
            <div style={{ position: 'sticky', left: 0, top: 0, background: '#f9fafb', zIndex: 2, borderBottom: '1px solid #eee', padding: '8px 12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Agent</div>
            {ticks.map((b, i) => (
              <div key={`agt-tk-${i}`} style={{ position: 'sticky', top: 0, borderBottom: '1px solid #e5e7eb', background: '#f3f4f6', zIndex: 2, padding: '4px 6px' }} />
            ))}
            {/* Header line overlay aligned with tick grid lines (agent) */}
            <div aria-hidden style={{ position: 'absolute', left: `${LEFT_COL_PX}px`, right: 0, top: 0, height: 28, pointerEvents: 'none', zIndex: 2 }}>
              {ticks.map((tk, i) => {
                const posPct = Math.max(0, Math.min(100, ((tk.toMillis() - tStartMs) / tSpanMs) * 100));
                const isHour = tk.minute === 0;
                return (
                  <div key={`agt-headline-${i}`} style={{ position: 'absolute', left: `${posPct}%`, top: 0, bottom: 0, width: isHour ? 2 : 1, background: isHour ? '#cbd5e1' : '#e9edf3', opacity: isHour ? 0.7 : 0.45 }} />
                );
              })}
            </div>
            {/* Header label overlay centered on lines (agent) */}
            <div style={{ position: 'absolute', left: `${LEFT_COL_PX}px`, right: 0, top: 0, height: 28, pointerEvents: 'none', zIndex: 3 }}>
              {ticks.map((b, i) => {
                const isHour = b.minute === 0;
                const label = b.toFormat('HH:mm');
                const posPct = Math.max(0, Math.min(100, ((b.toMillis() - tStartMs) / tSpanMs) * 100));
                const onClick = (e) => {
                  e.stopPropagation();
                  const span = Math.max(1000, view.endMs - view.startMs);
                  const center = b.toMillis();
                  let newStart = Math.max(domain.startMs, Math.min(center - span / 2, domain.endMs - span));
                  let newEnd = newStart + span;
                  setView({ startMs: newStart, endMs: newEnd });
                };
                const onDoubleClick = (e) => {
                  e.stopPropagation();
                  const span = Math.max(1000, view.endMs - view.startMs);
                  const newSpan = Math.max(60 * 1000, Math.round(span / 2));
                  const center = b.toMillis();
                  let newStart = Math.max(domain.startMs, Math.min(center - newSpan / 2, domain.endMs - newSpan));
                  let newEnd = newStart + newSpan;
                  setView({ startMs: newStart, endMs: newEnd });
                };
                return (
                  <div
                    key={`agt-label-${i}`}
                    onClick={onClick}
                    onDoubleClick={onDoubleClick}
                    title="Click to center here • Double-click to zoom in"
                    style={{ position: 'absolute', left: `${posPct}%`, top: 2, transform: 'translateX(-50%)', fontSize: isHour ? 11 : 10, fontWeight: isHour ? 700 : 600, color: '#374151', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto' }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
            {/* rows */}
            {workers.map(w => (
              <React.Fragment key={w}>
                <div style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, borderRight: '1px solid #eee', padding: '6px 12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', boxShadow: (hoverTaskWorkerRoles && hoverTaskWorkerRoles.get(w)) ? `inset 0 0 0 2px ${hoverTaskWorkerRoles.get(w).color}` : 'none', borderRadius: 4 }} title={w}>
                  <div style={{ fontWeight: 600, color: (hoverTaskWorkerRoles && hoverTaskWorkerRoles.get(w)) ? hoverTaskWorkerRoles.get(w).color : '#111827', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w}</div>
                </div>
                {/* Single lane per worker with exact activity segments */}
                <div style={{ gridColumn: `2 / span ${ticks.length}`, position: 'relative', height: 22 }}>
                  {(segmentsByWorker[w] || []).map((seg, idx) => {
                  const segStartMs = Math.max(view.startMs, seg.start.toMillis());
                  const segEndMs = Math.min(view.endMs, seg.end.toMillis());
                  if (segEndMs <= segStartMs) return null;
                  const startPct = Math.max(0, Math.min(100, ((segStartMs - tStartMs) / tSpanMs) * 100));
                  const endPct = Math.max(0, Math.min(100, ((segEndMs - tStartMs) / tSpanMs) * 100));
                  const widthPct = Math.max(0, endPct - startPct);
                  const minPx = 2; // ensure ultra-short segments are visible
                  const color = seg.label ? stringToColor(seg.label) : '#e5e7eb';
                  const dimsFromActivityLegend = hoverLabel && seg.label !== hoverLabel;
                  const overlaps = hoverTaskWindow && (seg.end.toMillis() > hoverTaskWindow.startMs) && (seg.start.toMillis() < hoverTaskWindow.endMs);
                  let dimsFromTaskHover = false;
                  if (hoverTaskWorkerRoles) {
                    const role = hoverTaskWorkerRoles.get(w)?.role;
                    if (role === 'handled') {
                      // Only keep the 'on call' segment visible for the handled worker during the task window
                      dimsFromTaskHover = !(isCall(seg.label) && overlaps);
                    } else if (role === 'rejected' || role === 'missed') {
                      // Contributors: dim all segments; name glow indicates involvement
                      dimsFromTaskHover = true;
                    } else if (role == null) {
                      // Not involved: dim
                      dimsFromTaskHover = true;
                    }
                  } else if (hoverTaskWorkers) {
                    // backward-compatibility if roles map isn't populated for some reason
                    dimsFromTaskHover = !hoverTaskWorkers.has(w) || !(isCall(seg.label) && overlaps);
                  }
                  // Focus dimming for agent timeline: if a specific activity segment is hovered, dim all others
                  const thisAgentSegKey = `${w}-${seg.label}-${seg.start.toMillis?.() ?? ''}-${seg.end.toMillis?.() ?? ''}`;
                  let dimmed = false;
                  if (hoverAgentSegKey) {
                    dimmed = hoverAgentSegKey !== thisAgentSegKey;
                  } else {
                    dimmed = dimsFromActivityLegend || dimsFromTaskHover;
                  }
                  const overlapHighlight = (hoverTaskWorkerRoles && (hoverTaskWorkerRoles.get(w)?.role === 'handled') && isCall(seg.label) && overlaps) || (hoverTaskWorkers && hoverTaskWorkers.has(w) && isCall(seg.label) && overlaps);
                  return (
                    <div
                      key={`seg-${idx}`}
                      style={{ position: 'absolute', left: `${startPct}%`, width: widthPct < 0.2 ? minPx : `${widthPct}%`, top: 2, bottom: 2, background: color, opacity: dimmed ? 0.3 : 1, borderLeft: '1px solid rgba(0,0,0,0.25)', borderRight: '1px solid rgba(0,0,0,0.25)', borderRadius: 2, zIndex: 1, boxShadow: overlapHighlight ? 'inset 0 0 0 2px #3b82f6' : 'none' }}
                      onMouseEnter={(e) => {
                        const durSec = Math.max(1, Math.round((seg.end.toMillis() - seg.start.toMillis()) / 1000));
                        const durStr = durSec < 60 ? `${durSec}s` : `${Math.floor(durSec/60)}m ${durSec%60}s`;
                        const pos = placeTooltip(e.clientX, e.clientY);
                        if (hoverLabel) setHoverLabel(null);
                        setHoverAgentSegKey(thisAgentSegKey);
                        // reverse hover linking: if this is an on-call segment, highlight corresponding task(s)
                        const sMs = seg.start.toMillis();
                        const eMs = seg.end.toMillis();
                        setHoverWorkerTarget(isCall(seg.label) ? { worker: w, startMs: sMs, endMs: eMs } : null);
                        setTooltip({
                          visible: true,
                          x: pos.x,
                          y: pos.y,
                          lines: [
                            `${w} · ${seg.label}`,
                            `Start: ${seg.start.toFormat('HH:mm:ss')}`,
                            `Einde: ${seg.end.toFormat('HH:mm:ss')}`,
                            `Duur: ${durStr}`
                          ]
                        });
                      }}
                      onMouseMove={(e) => moveTooltip(e.clientX, e.clientY)}
                      onMouseLeave={() => { setTooltip(t => ({ ...t, visible: false })); setHoverWorkerTarget(null); setHoverAgentSegKey(null); }}
                    />
                  );
                  })}
                </div>
              </React.Fragment>
            ))}
            {/* One-time tick overlay for the entire agent grid */}
            <div aria-hidden style={{ position: 'absolute', left: `${LEFT_COL_PX}px`, right: 0, top: 0, bottom: 0, pointerEvents: 'none', zIndex: 0 }}>
              {ticks.map((tk, i) => {
                const posPct = Math.max(0, Math.min(100, ((tk.toMillis() - tStartMs) / tSpanMs) * 100));
                const isHour = tk.minute === 0;
                return (
                  <div key={`vline-grid-${i}`} style={{ position: 'absolute', left: `${posPct}%`, top: 0, bottom: 0, width: isHour ? 2 : 1, background: isHour ? '#cbd5e1' : '#e9edf3', opacity: isHour ? 0.7 : 0.45 }} />
                );
              })}
            </div>
          </div>
          {/* Drag selection overlay (visual) */}
          {drag.active && containerRef.current && (() => {
            const rect = containerRef.current.getBoundingClientRect();
            const columnsLeft = rect.left + LEFT_COL_PX;
            const leftPx = Math.min(drag.startX, drag.currentX) - columnsLeft;
            const widthPx = Math.abs(drag.currentX - drag.startX);
            const scrollLeft = containerRef.current.scrollLeft || 0;
            return (
              <div style={{ position: 'absolute', left: scrollLeft + LEFT_COL_PX + leftPx, top: HEADER_PX, height: `calc(100% - ${HEADER_PX}px)`, width: widthPx, background: 'rgba(5,127,162,0.15)', border: '1px solid rgba(5,127,162,0.45)', pointerEvents: 'none' }} />
            );
          })()}
          </div>
        </ChartContainer>
  {/* hover tooltip */}
      {tooltip.visible && (
        <div ref={tooltipElRef} style={{ position: 'absolute', transform: `translate3d(${tooltip.x}px, ${tooltip.y}px, 0)`, background: 'rgba(17,24,39,0.95)', color: '#fff', padding: '6px 8px', borderRadius: 6, fontSize: 12, pointerEvents: 'none', zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: 360, wordBreak: 'break-word', willChange: 'transform' }}>
          {tooltip.lines.filter(Boolean).map((l, idx) => <div key={idx}>{l}</div>)}
        </div>
      )}
  {/* Summary metrics per worker (counts, average durations, delta vs team avg) */}
      {workers.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Overzicht per Agent (in huidig zicht)</div>
          <div style={{ overflowX: 'auto' }}>
            {(() => {
              const categories = [
                { key: 'call', label: 'Calls' },
                { key: 'shortBreak', label: 'Short breaks' },
                { key: 'adminCF', label: 'Admin Internal – Customer Feedback' },
                { key: 'adminOther', label: 'Admin Internal – Other' },
                { key: 'ready', label: 'Ready' },
              ];
              const deltaChip = (delta) => (
                <span title="Verschil met teamgemiddelde" style={{ display: 'inline', textAlign: 'right', color: delta == null ? '#6b7280' : (delta >= 0 ? '#b91c1c' : '#065f46'), fontVariantNumeric: 'tabular-nums' }}>
                  {delta == null ? '—' : `${delta >= 0 ? '+' : '−'}${fmt(Math.abs(delta))}`}
                </span>
              );
              // Legend-based soft tint per category
              const catBg = (key) => {
                const byKey = {
                  call: stringToColor('Handling Call Session'),
                  shortBreak: stringToColor('Not Ready - Short Break'),
                  adminCF: stringToColor('Admin Internal - Customer Feedback'),
                  adminOther: stringToColor('Admin Internal - Other'),
                  ready: stringToColor('Ready'),
                };
                // turn HSL into rgba-ish soft bg: we can layer opacity via linear-gradient
                const base = byKey[key] || '#e5e7eb';
                return base;
              };
              return (
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1100, fontSize: 12, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 220 }} />
                    <col style={{ width: 140 }} />
                    {categories.flatMap((c, idx) => [
                      <col key={`${c.key}-col-1`} style={{ width: 60 }} />,
                      <col key={`${c.key}-col-2`} style={{ width: 84 }} />,
                      <col key={`${c.key}-col-3`} style={{ width: 84 }} />,
                    ])}
                  </colgroup>
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}>Agent</th>
                      <th rowSpan={2} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', borderRight: '2px solid #e5e7eb' }}>Team</th>
                      {categories.map((cat, i) => (
                        <th key={cat.key} colSpan={3} style={{ textAlign: 'center', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', borderRight: i === categories.length - 1 ? '0' : '2px solid #e5e7eb' }}>{cat.label}</th>
                      ))}
                    </tr>
                    <tr>
                      {categories.flatMap((cat, i) => ([
                        <th key={`${cat.key}-count`} style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>#</th>,
                        <th key={`${cat.key}-avg`} style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Avg</th>,
                        <th key={`${cat.key}-delta`} style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', borderRight: i === categories.length - 1 ? '0' : '2px solid #e5e7eb', background: '#f9fafb' }}>Δ team</th>,
                      ]))}
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map((w, idx) => {
                      const m = metrics.perWorker[w];
                      const rowBg = idx % 2 === 0 ? '#fff' : '#fbfbfc';
                      const cell = (rec, avg, delta, i) => (
                        <>
                            <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: `linear-gradient(0deg, ${catBg(categories[i].key)}22, ${catBg(categories[i].key)}22), ${rowBg}`, fontVariantNumeric: 'tabular-nums' }}>{rec.count ?? 0}</td>
                            <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: `linear-gradient(0deg, ${catBg(categories[i].key)}22, ${catBg(categories[i].key)}22), ${rowBg}`, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{fmt(avg)}</td>
                            <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: `linear-gradient(0deg, ${catBg(categories[i].key)}22, ${catBg(categories[i].key)}22), ${rowBg}`, borderRight: i === categories.length - 1 ? '0' : '2px solid #e5e7eb' }}>{deltaChip(delta)}</td>
                        </>
                      );
                      return (
                        <tr key={w}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', position: 'sticky', left: 0, background: rowBg, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={w}>{w}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg }}>{m?.team || '—'}</td>
                          {cell(m?.call || {count:0}, m?.avgs?.callAvg, m?.deltas?.call, 0)}
                          {cell(m?.shortBreak || {count:0}, m?.avgs?.shortBreakAvg, m?.deltas?.shortBreak, 1)}
                          {cell(m?.adminCF || {count:0}, m?.avgs?.adminCFAvg, m?.deltas?.adminCF, 2)}
                          {cell(m?.adminOther || {count:0}, m?.avgs?.adminOtherAvg, m?.deltas?.adminOther, 3)}
                          {cell(m?.ready || {count:0}, m?.avgs?.readyAvg, m?.deltas?.ready, 4)}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}
      {/* Inbound call summaries by Service Group (Queue) */}
      {inboundCallSummaries?.byQueue?.length > 0 && (
        <div style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Inkomende oproepen per Queue (in huidig zicht)</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900, fontSize: 12, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 260 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Queue</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>#</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}># / % abandoned</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Avg queue</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Avg talk</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}># rejected</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}># missed</th>
                </tr>
              </thead>
              <tbody>
                {inboundCallSummaries.byQueue.map((row, idx) => {
                  const rowBg = idx % 2 === 0 ? '#fff' : '#fbfbfc';
                  const pct = (v) => `${Math.round((v || 0) * 100)}%`;
                  const fmtSec = (s) => {
                    if (s == null || !isFinite(s)) return '—';
                    const r = Math.round(s);
                    return r < 60 ? `${r}s` : `${Math.floor(r/60)}m ${r%60}s`;
                  };
                  return (
                    <tr key={row.name}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg }}>{row.name}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{row.total}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{`${row.canceled} / ${pct(row.canceledPct)}`}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{fmtSec(row.avgQueueSec)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{fmtSec(row.avgTalkSec)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{row.rejected}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{row.missed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Inbound call summaries by Customer */}
      {inboundCallSummaries?.byCustomer?.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid #eee', paddingTop: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Inkomende oproepen per Klant (in huidig zicht)</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900, fontSize: 12, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 260 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 90 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Klant</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>#</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}># / % abandoned</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Avg queue</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Avg talk</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}># rejected</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}># missed</th>
                </tr>
              </thead>
              <tbody>
                {inboundCallSummaries.byCustomer.map((row, idx) => {
                  const rowBg = idx % 2 === 0 ? '#fff' : '#fbfbfc';
                  const pct = (v) => `${Math.round((v || 0) * 100)}%`;
                  const fmtSec = (s) => {
                    if (s == null || !isFinite(s)) return '—';
                    const r = Math.round(s);
                    return r < 60 ? `${r}s` : `${Math.floor(r/60)}m ${r%60}s`;
                  };
                  return (
                    <tr key={row.name}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg }}>{row.name}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{row.total}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{`${row.canceled} / ${pct(row.canceledPct)}`}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{fmtSec(row.avgQueueSec)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{fmtSec(row.avgTalkSec)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{row.rejected}</td>
                      <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f3f4f6', background: rowBg, fontVariantNumeric: 'tabular-nums' }}>{row.missed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Fixed bottom-center Reset zoom button */}
      {(view.startMs !== domain.startMs || view.endMs !== domain.endMs) && (
        <div style={{ position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 10000 }}>
          <button
            onClick={() => setView({ startMs: domain.startMs, endMs: domain.endMs })}
            className="wep-button wep-button--outline"
            style={{ boxShadow: '0 6px 16px rgba(0,0,0,0.25)', background: '#ffffff' }}
            title="Reset zoom"
          >
            Reset zoom
          </button>
        </div>
      )}
      {/* Occupancy chart removed temporarily */}
    </div>
  );
}
